import React, { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  Fingerprint,
  LoaderCircle,
  LockKeyhole,
  Mail,
  MessageCircleMore,
  Phone,
  RotateCcw,
  UserRound,
  UserRoundPlus,
} from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import fetchData from "../../api/fetchApi";
import { getLang } from "../../i18n";
import InternationalPhoneField from "../forms/InternationalPhoneField";
import { isInternationalPhoneValid, phoneForApi, toE164Phone } from "../../utils/phone";

const COPY = {
  ar: {
    home: "واتسي - الرئيسية",
    brand: "واتسي",
    eyebrow: "ابدأ مساحة عملك",
    title: "حساب واحد لكل محادثات عملك",
    description: "تحقق من رقمك، أنشئ حسابك، ثم اربط جلسة واتساب من لوحة التحكم.",
    benefitOne: "صندوق وارد موحد للفريق",
    benefitTwo: "حملات وردود آلية بضوابط واضحة",
    benefitThree: "مفاتيح API وتقارير استخدام من مكان واحد",
    step: "الخطوة",
    of: "من",
    verifyTitle: "تحقق من رقم الهاتف",
    verifyDescription: "سنرسل رمزاً من 6 أرقام للتأكد أن الرقم يخصك.",
    accountTitle: "أكمل بيانات الحساب",
    accountDescription: "استخدم بيانات واضحة لتسهيل إدارة الفريق لاحقاً.",
    phone: "رقم الهاتف",
    phonePlaceholder: "مثال: 2189XXXXXXXX",
    sendCode: "إرسال الرمز",
    sendingCode: "جاري الإرسال…",
    code: "رمز التحقق",
    codePlaceholder: "000000",
    verify: "تحقق من الرمز",
    verifying: "جاري التحقق…",
    resend: "إعادة إرسال الرمز",
    changePhone: "تغيير الرقم",
    username: "اسم المستخدم",
    usernamePlaceholder: "بدون مسافات",
    fullName: "الاسم الكامل",
    fullNamePlaceholder: "الاسم كما سيظهر للفريق",
    email: "البريد الإلكتروني",
    optional: "اختياري",
    emailPlaceholder: "name@company.com",
    birthday: "تاريخ الميلاد",
    password: "كلمة المرور",
    passwordPlaceholder: "8 أحرف على الأقل",
    confirm: "تأكيد كلمة المرور",
    confirmPlaceholder: "أعد كتابة كلمة المرور",
    showPassword: "إظهار كلمة المرور",
    hidePassword: "إخفاء كلمة المرور",
    create: "إنشاء الحساب",
    creating: "جاري إنشاء الحساب…",
    passwordHint: "استخدم 8 أحرف على الأقل.",
    loginPrompt: "لديك حساب بالفعل؟",
    login: "تسجيل الدخول",
    phoneRequired: "أدخل رقم الهاتف أولاً.",
    phoneInvalid: "اختر الدولة وأدخل رقم هاتف صحيحاً.",
    codeRequired: "أدخل رمز التحقق المكون من 6 أرقام.",
    codeSent: "أرسلنا رمز التحقق إلى رقمك.",
    sendFailed: "تعذر إرسال رمز التحقق. حاول مرة أخرى.",
    verified: "تم التحقق من الرقم. أكمل بيانات حسابك.",
    verifyFailed: "رمز التحقق غير صحيح أو انتهت صلاحيته.",
    mismatch: "كلمتا المرور غير متطابقتين.",
    registerFailed: "تعذر إنشاء الحساب. راجع البيانات وحاول مرة أخرى.",
    successTitle: "حسابك جاهز",
    successDescription: "تم إنشاء الحساب. سجل الدخول لإعداد مساحة العمل وربط جلسة واتساب.",
    continueLogin: "الانتقال إلى تسجيل الدخول",
  },
  en: {
    home: "Whatsy home",
    brand: "Whatsy",
    eyebrow: "Start your workspace",
    title: "One account for every business conversation",
    description: "Verify your number, create your account, then connect WhatsApp from the dashboard.",
    benefitOne: "A shared team inbox",
    benefitTwo: "Campaigns and automations with clear controls",
    benefitThree: "API keys and usage reporting in one place",
    step: "Step",
    of: "of",
    verifyTitle: "Verify your phone number",
    verifyDescription: "We’ll send a six-digit code to confirm the number belongs to you.",
    accountTitle: "Complete your account",
    accountDescription: "Use clear details so managing your team stays simple later.",
    phone: "Phone number",
    phonePlaceholder: "Example: 2189XXXXXXXX",
    sendCode: "Send code",
    sendingCode: "Sending…",
    code: "Verification code",
    codePlaceholder: "000000",
    verify: "Verify code",
    verifying: "Verifying…",
    resend: "Send another code",
    changePhone: "Change number",
    username: "Username",
    usernamePlaceholder: "No spaces",
    fullName: "Full name",
    fullNamePlaceholder: "The name your team will see",
    email: "Email address",
    optional: "Optional",
    emailPlaceholder: "name@company.com",
    birthday: "Date of birth",
    password: "Password",
    passwordPlaceholder: "At least 8 characters",
    confirm: "Confirm password",
    confirmPlaceholder: "Type your password again",
    showPassword: "Show password",
    hidePassword: "Hide password",
    create: "Create account",
    creating: "Creating account…",
    passwordHint: "Use at least eight characters.",
    loginPrompt: "Already have an account?",
    login: "Sign in",
    phoneRequired: "Enter your phone number first.",
    phoneInvalid: "Choose a country and enter a valid phone number.",
    codeRequired: "Enter the six-digit verification code.",
    codeSent: "We sent a verification code to your number.",
    sendFailed: "We couldn’t send the verification code. Try again.",
    verified: "Your number is verified. Complete your account details.",
    verifyFailed: "The code is incorrect or has expired.",
    mismatch: "The passwords do not match.",
    registerFailed: "We couldn’t create the account. Check the details and try again.",
    successTitle: "Your account is ready",
    successDescription: "Sign in to set up your workspace and connect your WhatsApp session.",
    continueLogin: "Continue to sign in",
  },
};

export default function RegisterPage() {
  const lang = getLang();
  const isArabic = lang === "ar";
  const copy = COPY[isArabic ? "ar" : "en"];
  const DirectionArrow = isArabic ? ArrowLeft : ArrowRight;

  const [username, setUsername] = useState("");
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [birthday, setBirthday] = useState("");
  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [notice, setNotice] = useState(null);
  const [registered, setRegistered] = useState(false);

  if (localStorage.getItem("access_token")) {
    return <Navigate to="/admin" replace />;
  }

  const normalizedPhone = phoneForApi(phone);
  const displayPhone = toE164Phone(phone);
  const phoneValid = isInternationalPhoneValid(phone);

  const handlePhoneChange = (nextPhone) => {
    setPhone(nextPhone);
    setOtpSent(false);
    setOtpVerified(false);
    setOtpCode("");
    setNotice(null);
  };

  const handleSendOtp = async (event) => {
    event.preventDefault();
    if (!normalizedPhone) {
      setNotice({ type: "error", text: copy.phoneRequired });
      return;
    }
    if (!phoneValid) {
      setNotice({ type: "error", text: copy.phoneInvalid });
      return;
    }

    setSendingOtp(true);
    setNotice(null);
    try {
      const response = await fetchData(
        "send_otp",
        "POST",
        JSON.stringify({ phone_number: normalizedPhone })
      );
      if (response?.message !== "OTP sent successfully") throw new Error(copy.sendFailed);
      setOtpSent(true);
      setNotice({ type: "success", text: copy.codeSent });
    } catch (sendError) {
      setNotice({ type: "error", text: sendError.message || copy.sendFailed });
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    if (!normalizedPhone || otpCode.length !== 6) {
      setNotice({ type: "error", text: copy.codeRequired });
      return;
    }

    setVerifyingOtp(true);
    setNotice(null);
    try {
      const response = await fetchData(
        "verify_otp",
        "POST",
        JSON.stringify({ phone_number: normalizedPhone, otp_code: otpCode })
      );
      if (response?.message !== "Phone number verified successfully") {
        throw new Error(copy.verifyFailed);
      }
      setOtpVerified(true);
      setNotice({ type: "success", text: copy.verified });
    } catch (verifyError) {
      setOtpVerified(false);
      setNotice({ type: "error", text: verifyError.message || copy.verifyFailed });
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setNotice(null);
    if (!otpVerified) {
      setNotice({ type: "error", text: copy.phoneRequired });
      return;
    }
    if (password !== confirm) {
      setNotice({ type: "error", text: copy.mismatch });
      return;
    }

    setLoading(true);
    try {
      const response = await fetchData(
        "/signup",
        "POST",
        JSON.stringify({
          username: username.trim(),
          password,
          name: fullname.trim(),
          phone_number: normalizedPhone,
          email: email.trim(),
          birthday,
        })
      );
      if (!response || response.success === false) {
        throw new Error(response?.message || copy.registerFailed);
      }
      setRegistered(true);
    } catch (registerError) {
      setNotice({ type: "error", text: registerError.message || copy.registerFailed });
    } finally {
      setLoading(false);
    }
  };

  const resetPhone = () => {
    setOtpVerified(false);
    setOtpSent(false);
    setOtpCode("");
    setNotice(null);
  };

  const activeStep = otpVerified ? 2 : 1;

  return (
    <div className="auth-shell register-page" dir={isArabic ? "rtl" : "ltr"}>
      <Link className="auth-brand" to="/" aria-label={copy.home}>
        <span className="auth-brand-mark"><MessageCircleMore size={24} /></span>
        <span>{copy.brand}</span>
      </Link>

      <section className="register-card" aria-labelledby="register-title">
        <aside className="register-aside">
          <div className="register-aside-content">
            <span className="eyebrow eyebrow-light">{copy.eyebrow}</span>
            <h1 id="register-title">{copy.title}</h1>
            <p>{copy.description}</p>
            <ul className="register-benefits">
              {[copy.benefitOne, copy.benefitTwo, copy.benefitThree].map((benefit) => (
                <li key={benefit}><Check size={16} /> <span>{benefit}</span></li>
              ))}
            </ul>
          </div>

          <div className="register-step-list" aria-label={`${copy.step} ${activeStep} ${copy.of} 2`}>
            {[copy.verifyTitle, copy.accountTitle].map((label, index) => {
              const step = index + 1;
              const complete = activeStep > step;
              const active = activeStep === step;
              return (
                <div className={`register-step ${active ? "register-step-active" : ""}`} key={label}>
                  <span>{complete ? <Check size={15} /> : step}</span>
                  <div><small>{copy.step} {step}</small><strong>{label}</strong></div>
                </div>
              );
            })}
          </div>
        </aside>

        <section className="register-main">
          {registered ? (
            <div className="register-success" role="status">
              <span className="register-success-icon"><CheckCircle2 size={30} /></span>
              <h2>{copy.successTitle}</h2>
              <p>{copy.successDescription}</p>
              <Link to="/auth/login" className="primary-button">
                {copy.continueLogin} <DirectionArrow size={18} />
              </Link>
            </div>
          ) : (
            <>
              <div className="register-progress" aria-hidden="true">
                <span style={{ width: `${activeStep * 50}%` }} />
              </div>
              <header className="auth-card-copy register-heading">
                <span className="eyebrow">{copy.step} {activeStep} {copy.of} 2</span>
                <h2>{otpVerified ? copy.accountTitle : copy.verifyTitle}</h2>
                <p>{otpVerified ? copy.accountDescription : copy.verifyDescription}</p>
              </header>

              {notice && (
                <div className={`notice ${notice.type === "success" ? "notice-success" : "notice-error"}`} role={notice.type === "error" ? "alert" : "status"}>
                  {notice.type === "success" ? <CheckCircle2 size={18} /> : <Fingerprint size={18} />}
                  <span>{notice.text}</span>
                </div>
              )}

              {!otpVerified ? (
                <div className="register-form-stack">
                  <form onSubmit={handleSendOtp} className="auth-form register-phone-form">
                    <label className="field-label" htmlFor="register-phone">{copy.phone}</label>
                    <div className="register-action-row">
                      <InternationalPhoneField
                        id="register-phone"
                        className="register-phone-field"
                        value={phone}
                        onChange={handlePhoneChange}
                        language={isArabic ? "ar" : "en"}
                        placeholder={copy.phonePlaceholder}
                        autoFocus
                        required
                      />
                      <button className="secondary-button register-inline-button" disabled={sendingOtp || !phoneValid}>
                        {sendingOtp ? <LoaderCircle className="animate-spin" size={18} /> : <Phone size={18} />}
                        {sendingOtp ? copy.sendingCode : otpSent ? copy.resend : copy.sendCode}
                      </button>
                    </div>
                  </form>

                  {otpSent && (
                    <form onSubmit={handleVerifyOtp} className="auth-form register-code-form">
                      <label className="field-label" htmlFor="register-code">{copy.code}</label>
                      <input id="register-code" type="text" className="field-control code-input" value={otpCode} onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder={copy.codePlaceholder} maxLength={6} dir="ltr" required />
                      <button className="primary-button" disabled={verifyingOtp || otpCode.length !== 6}>
                        {verifyingOtp ? <LoaderCircle className="animate-spin" size={18} /> : <Fingerprint size={18} />}
                        {verifyingOtp ? copy.verifying : copy.verify}
                      </button>
                    </form>
                  )}
                </div>
              ) : (
                <form onSubmit={handleRegister} className="auth-form register-details-form">
                  <div className="verified-phone-row">
                    <span><CheckCircle2 size={18} /> <bdi dir="ltr">{displayPhone}</bdi></span>
                    <button type="button" onClick={resetPhone}><RotateCcw size={15} /> {copy.changePhone}</button>
                  </div>

                  <div className="register-fields-grid">
                    <div>
                      <label className="field-label" htmlFor="register-username">{copy.username}</label>
                      <div className="field-with-icon"><UserRoundPlus size={18} /><input id="register-username" className="field-control" value={username} onChange={(event) => setUsername(event.target.value.replace(/\s/g, ""))} autoComplete="username" placeholder={copy.usernamePlaceholder} required /></div>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="register-name">{copy.fullName}</label>
                      <div className="field-with-icon"><UserRound size={18} /><input id="register-name" className="field-control" value={fullname} onChange={(event) => setFullname(event.target.value)} autoComplete="name" placeholder={copy.fullNamePlaceholder} required /></div>
                    </div>
                    <div>
                      <div className="field-heading"><label className="field-label" htmlFor="register-email">{copy.email}</label><span className="field-optional">{copy.optional}</span></div>
                      <div className="field-with-icon"><Mail size={18} /><input id="register-email" type="email" className="field-control" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder={copy.emailPlaceholder} dir="ltr" /></div>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="register-birthday">{copy.birthday}</label>
                      <div className="field-with-icon"><CalendarDays size={18} /><input id="register-birthday" type="date" className="field-control" value={birthday} onChange={(event) => setBirthday(event.target.value)} required /></div>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="register-password">{copy.password}</label>
                      <div className="password-field field-with-icon"><LockKeyhole size={18} /><input id="register-password" type={showPassword ? "text" : "password"} className="field-control" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} autoComplete="new-password" placeholder={copy.passwordPlaceholder} required /><button type="button" className="password-toggle" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? copy.hidePassword : copy.showPassword}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="register-confirm">{copy.confirm}</label>
                      <div className="password-field field-with-icon"><LockKeyhole size={18} /><input id="register-confirm" type={showConfirm ? "text" : "password"} className="field-control" value={confirm} onChange={(event) => setConfirm(event.target.value)} minLength={8} autoComplete="new-password" placeholder={copy.confirmPlaceholder} required /><button type="button" className="password-toggle" onClick={() => setShowConfirm((visible) => !visible)} aria-label={showConfirm ? copy.hidePassword : copy.showPassword}>{showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}</button></div>
                    </div>
                  </div>

                  <div className="password-hint"><span className={password.length >= 8 ? "password-hint-valid" : ""}><Check size={14} /></span>{copy.passwordHint}</div>
                  <button type="submit" className="primary-button" disabled={loading || password.length < 8 || confirm.length < 8}>
                    {loading ? <LoaderCircle className="animate-spin" size={18} /> : <UserRoundPlus size={18} />}
                    {loading ? copy.creating : copy.create}
                    {!loading && <DirectionArrow size={18} />}
                  </button>
                </form>
              )}
            </>
          )}

          {!registered && <p className="auth-card-footer register-footer">{copy.loginPrompt}{" "}<Link to="/auth/login" className="text-link strong">{copy.login}</Link></p>}
        </section>
      </section>
    </div>
  );
}
